import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CircleQuestionMark, X } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { helpForPage, HELP_LEVEL_LABELS } from '@/lib/help'
import { useFeatures } from '@/lib/features'
import { cn } from '@/lib/cn'

/**
 * Hjälp där man står.
 *
 * En hjälpsida man måste leta upp läses av den som redan bestämt sig för att
 * fråga. Den som fastnat mitt i något tittar sig omkring på sidan hen är på –
 * och där ska svaret finnas.
 *
 * Knappen visar bara det som gäller den aktuella sidan och den egna rollen.
 * En lagledare på lagsidan får hjälp om avstämning och påminnelser, inte om
 * backup; en köpare i häftet får veta hur man håller in en rabatt, inte hur
 * man genererar koder.
 */

/** Sidor där knappen är i vägen snarare än till hjälp. */
const HIDDEN_ON = ['/logga-in', '/skapa-konto', '/glomt-losenord', '/hjalp']

export function HelpButton() {
  const { role, user } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  const { features } = useFeatures()
  const topics = useMemo(
    () => helpForPage(pathname, role, features),
    [pathname, role, features],
  )

  // Panelen ska inte följa med till nästa sida.
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Utloggad, på en sida utan hjälp, eller på en sida där knappen stör.
  if (!user || topics.length === 0) return null
  if (HIDDEN_ON.some((path) => pathname.startsWith(path))) return null

  return (
    <>
      {open && (
        <>
          {/* Skuggan fångar klick utanför. Den ligger under panelen men över
              sidan, så att ett klick vid sidan om stänger i stället för att
              råka träffa något i innehållet. */}
          <button
            type="button"
            aria-label="Stäng hjälpen"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-black/30"
          />

          <div
            role="dialog"
            aria-label={`Hjälp för den här sidan`}
            className={cn(
              'fixed right-4 left-4 z-50 flex max-h-[70dvh] flex-col overflow-hidden rounded-card',
              'border border-border bg-surface shadow-lift',
              // Ovanför både knappen och bottennavigeringen.
              'bottom-[calc(env(safe-area-inset-bottom)+9.5rem)] sm:bottom-24 sm:left-auto sm:w-96',
            )}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-extrabold">Hjälp för den här sidan</p>
                <p className="truncate text-xs text-muted">
                  {[...new Set(topics.map((topic) => HELP_LEVEL_LABELS[topic.level]))].join(' · ')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Stäng"
                className="tap-safe -mr-1 shrink-0 rounded-pill p-1 text-muted hover:text-ink"
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
              {topics.map((topic) => (
                <details
                  key={topic.id}
                  className="group rounded-card bg-surface-2 px-3.5 py-2.5"
                >
                  {/* Hela raden är träffytan, inte bara texten. Den som mest
                      behöver hjälpen har också svårast att träffa 20 px. */}
                  <summary className="flex min-h-11 cursor-pointer list-none items-center text-sm font-bold marker:hidden">
                    <span className="flex items-center justify-between gap-3">
                      {topic.question}
                      <span
                        aria-hidden
                        className="shrink-0 text-brand-ink transition-transform group-open:rotate-45"
                      >
                        +
                      </span>
                    </span>
                  </summary>
                  <p className="mt-2 text-sm text-muted">{topic.answer}</p>
                  {topic.steps && (
                    <ol className="mt-2.5 space-y-1.5">
                      {topic.steps.map((step, index) => (
                        <li key={step} className="flex gap-2 text-sm">
                          <span className="flex size-5 shrink-0 items-center justify-center rounded-pill bg-brand-soft text-xs font-bold text-brand-ink">
                            {index + 1}
                          </span>
                          <span className="text-muted">{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </details>
              ))}
            </div>

            <div className="border-t border-border px-4 py-3">
              <Link
                to="/hjalp"
                className="text-sm font-semibold text-brand-ink underline"
                onClick={() => setOpen(false)}
              >
                All hjälp och kontakt
              </Link>
            </div>
          </div>
        </>
      )}

      {/*
       * Placeringen sitter ovanför bottennavigeringen på mobil, annars hade
       * knappen legat ovanpå "Konto". På bredare skärmar finns ingen nav i
       * vägen och den kan gå längre ned.
       */}
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={open ? 'Stäng hjälpen' : 'Hjälp för den här sidan'}
        className={cn(
          'fixed right-4 z-50 flex size-12 items-center justify-center rounded-pill',
          'bg-brand text-on-brand shadow-lift transition-transform active:scale-95',
          'bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] sm:bottom-6',
        )}
      >
        {open ? (
          <X size={22} aria-hidden />
        ) : (
          <CircleQuestionMark size={22} aria-hidden />
        )}
      </button>
    </>
  )
}
